import { useNavigate } from "react-router";

import { Alert, Stack, Typography } from "@mui/material";

import { DataTable } from "@/common/components/table/DataTable";
import { clientPath } from "@/router/ClientRoutes";
import { useTranslation } from "@/translations";

import { ClientsFilters } from "./components/filter/ClientsFilters";
import { ClientsHeader } from "./components/header/ClientsHeader";
import { useClients } from "./hooks/useClients";

export function Clients() {
  const {
    clients,
    hasAdditionalClients,
    isLoading,
    isError,
    columnConfigurations,
    paginationResetKey,
    filters,
    onFiltersUpdate,
    removeAllFilters,
  } = useClients();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClientClick = (clientId: string) => {
    void navigate(clientPath.byId({ id: clientId }));
  };

  return (
    <Stack spacing={2} sx={{ height: "100%" }} pb={3}>
      <ClientsHeader
        numberOfClients={clients.length}
        hasAdditionalClients={hasAdditionalClients ?? false}
        isError={isError}
      />
      {filters && (
        <ClientsFilters filters={filters} onFiltersUpdate={onFiltersUpdate} onAllFiltersRemove={removeAllFilters} />
      )}
      {/* A failed search must never fall through to the empty state — "keine Kund:innen gefunden"
          would read as "this person is not a client" instead of "the search could not be run". */}
      {isError && <Alert severity="error">{t("clients.searchError")}</Alert>}
      {!isError &&
        (clients.length === 0 && !isLoading ? (
          <Typography sx={{ whiteSpace: "pre-wrap" }}>{t("clients.noResults")}</Typography>
        ) : (
          <Stack
            sx={{
              overflow: "auto",
              flex: 1,
              minHeight: 0,
            }}
          >
            <DataTable
              data={clients}
              columns={columnConfigurations}
              getRowId={(row) => row.clientId}
              onRowClick={handleClientClick}
              paginationResetKey={paginationResetKey}
            />
          </Stack>
        ))}
    </Stack>
  );
}
